import { type TimerProps as TProps, useIntl, useTimer, useTimerColors } from '@capsicum/common';
import { type AriaAttributes, type FC, type SVGProps } from 'react';
import styled, { useTheme } from 'styled-components';

export type TimerProps = TProps & {
	'aria-labelledby'?: AriaAttributes['aria-labelledby'];
	closedLabel?: boolean;
	customStatus?: string;
	customText?: string;
	icon?: FC<SVGProps<SVGSVGElement>>;
	iconStyle?: React.CSSProperties;
	size?: 'condensed' | 'default' | 'xsmall';
	testID?: string;
};

/**
 * Timer Component
 *
 * @param props.type  Timer color type: default = blue, colored = blue to red, red = expiring soon, darkBlue = dark blue
 * @param props.size Size of the timer
 * @param props.closedLabel If the timer should display a closed text after time is up
 * @param props.expirationDate End date of the timer
 * @param props.criticalDate Calculate time so that it is displayed in a different color
 * @param props.customFormat Format of the timer. e.g. '05:24:22' | '13 days' | '4h 0m'
 * @param props.customStatus Custom status based on which the timer will display a closed label
 * @param props.customText Custom text to display in the timer
 * @param props.onElapsed Function called when time is up
 * @param props.onExpiringSoon Function called when time is expiring soon
 * @returns {JSX.Element} Timer Component
 */

export const Timer = ({
	'aria-labelledby': ariaLabelledBy,
	closedLabel,
	customStatus,
	customText,
	icon: Icon,
	iconStyle,
	size = 'default',
	testID,
	...props
}: TimerProps): JSX.Element => {
	const { $t } = useIntl();
	const { sizing } = useTheme();
	const { color, formattedTime, hasExpired } = useTimer(props);
	const { timerFontColor, timerStyling } = useTimerColors(color);

	const acceptedStatuses = ['closed', 'finished'];

	return (
		<S.TimerContainer aria-labelledby={ariaLabelledBy} data-testid={testID} role="timer">
			{customText || !hasExpired || !closedLabel ? (
				<S.TimerWrapper $size={size} $timerStyling={timerStyling as unknown as string}>
					{Icon && (
						<S.StyledIcon
							$timerFontColor={timerFontColor as unknown as string}
							as={Icon}
							height={sizing.xsmall}
							style={iconStyle}
							width={sizing.xsmall}
						/>
					)}

					<S.LabelText
						$hasIcon={!!Icon}
						$size={size}
						$timerFontColor={timerFontColor as unknown as string}
						data-testid={`${testID}-time`}
					>
						{customText || formattedTime}
					</S.LabelText>
				</S.TimerWrapper>
			) : (
				((customStatus && acceptedStatuses.includes(customStatus)) || (hasExpired && closedLabel)) && (
					<S.ClosedWrapper $size={size}>
						<S.Closed $size={size}>{$t('rewards.auctions.closed')}</S.Closed>
					</S.ClosedWrapper>
				)
			)}
		</S.TimerContainer>
	);
};

namespace S {
	export const TimerContainer = styled.div`
		z-index: 1;
	`;

	export const TimerWrapper = styled.div<{
		$size: string;
		$timerStyling: string;
	}>`
		display: flex;
		flex-direction: row;
		${({ theme }) => theme.border.radius.small};
		${({ $timerStyling }) => $timerStyling};
		padding: ${({ $size, theme }) =>
			$size === 'condensed' || $size === 'xsmall'
				? `0 ${theme.spacings.xxsmall}`
				: `${theme.spacings.xxxsmall} ${theme.spacings.xxsmall}`};
	`;

	export const LabelText = styled.div<{
		$hasIcon: boolean;
		$size: string;
		$timerFontColor: string;
	}>`
		text-align: center;
		${({ $size, theme }) => ($size === 'xsmall' ? theme.fonts.text.caption1 : theme.fonts.text.text4)};
		${({ $timerFontColor }) => $timerFontColor};
		padding-left: ${({ $hasIcon, theme }) => ($hasIcon ? theme.spacings.xxxsmall : 0)};
	`;

	export const StyledIcon = styled.div<{
		$timerFontColor: string;
	}>`
		${({ $timerFontColor }) => $timerFontColor};
	`;

	export const Closed = styled.div<{
		$size: string;
	}>`
		${({ $size, theme }) => ($size === 'xsmall' ? theme.fonts.text.caption1 : theme.fonts.text.text4)};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;

	export const ClosedWrapper = styled.div<{
		$size: string;
	}>`
		${({ theme }) => theme.border.radius.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.subtle};
		border: ${({ theme }) => `1px solid ${theme.colors.borders.main.unselected}`};
		text-align: center;
		padding: ${({ $size, theme }) =>
			$size === 'condensed' || $size === 'xsmall'
				? `0 ${theme.spacings.xxsmall}`
				: `${theme.spacings.xxxsmall} ${theme.spacings.xxsmall}`};
		${({ theme }) => theme.border.radius.small};
	`;
}
